import { useState, useEffect, useCallback } from 'react';
import { getUserProfile } from '../api';

/**
 * Custom hook for managing user profile data
 * @returns {Object} Hook state and functions
 */
export const useUserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes in milliseconds

  const fetchProfile = useCallback(async (forceRefresh = false) => {
    // Check if we need to fetch based on cache duration
    if (!forceRefresh && lastFetchTime && profile) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION) {
        return; // Use cached data
      }
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getUserProfile();
      setProfile(response.user_info);
      setLastFetchTime(Date.now());
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err.message || 'Failed to fetch user profile');
    } finally {
      setLoading(false);
    }
  }, [lastFetchTime, profile]);

  // Initial fetch
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const refreshProfile = useCallback(() => {
    fetchProfile(true);
  }, [fetchProfile]);

  const updateProfileLocally = useCallback((updates) => {
    setProfile(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  const clearProfile = useCallback(() => {
    setProfile(null);
    setLastFetchTime(null);
  }, []);

  // Helper functions to get specific profile data
  const getSubscriptionStatus = useCallback(() => {
    return profile?.userprofile?.subscription_status || 'inactive';
  }, [profile]);

  const getSubscriptionPlan = useCallback(() => {
    return profile?.userprofile?.subscription_plan || null;
  }, [profile]);

  const getSocialAccounts = useCallback(() => {
    return profile?.userprofile?.social_accounts || [];
  }, [profile]);

  const getTimezone = useCallback(() => {
    return profile?.userprofile?.timezone || 'UTC';
  }, [profile]);

  const isOnboardingComplete = useCallback(() => {
    return profile?.userprofile?.is_onboarding_complete || false;
  }, [profile]);

  return {
    profile,
    loading,
    error,
    refreshProfile,
    updateProfileLocally,
    clearProfile,
    retry: refreshProfile,
    // Helper getters
    subscriptionStatus: getSubscriptionStatus(),
    subscriptionPlan: getSubscriptionPlan(),
    socialAccounts: getSocialAccounts(),
    timezone: getTimezone(),
    isOnboardingComplete: isOnboardingComplete(),
    // Last fetch info
    lastFetchTime,
    isCacheValid: lastFetchTime && (Date.now() - lastFetchTime < CACHE_DURATION)
  };
}; 